// ──────────────────────────────────────────────────────────────────────────
// Capa de datos del MÓDULO DE CONTABILIDAD — Riesgo Unido
//
// Filosofía contable (ver Manual dentro del módulo):
//  - La PRIMA NO es ingreso del corredor: es dinero en tránsito hacia la
//    aseguradora. El sistema ya lo separa (cobros_recibidos).
//  - El ÚNICO ingreso real del corredor es la COMISIÓN. Para fines fiscales,
//    lo que importa es la comisión NETA que le queda a RU (monto_ru),
//    porque lo pagado a agentes/referidos es un gasto ya descontado.
//  - Contabilidad de CAJA: el ingreso se cuenta cuando la aseguradora
//    efectivamente le pagó la comisión a RU (fecha_recibio_comision).
//
// Esta capa NO toca ninguna tabla existente: solo lee `comisiones` y escribe
// en las tablas nuevas con prefijo `cont_`.
// ──────────────────────────────────────────────────────────────────────────
import { supabase } from './engine';

// ===========================================================================
// CONFIGURACIÓN CONTABLE (una sola fila, id = 1)
// ===========================================================================
const CONFIG_DEFAULT = {
  id: 1,
  tipo_contribuyente: 'natural',
  inscrito_itbms: false,
  comisiones_itbms: false,
  cotiza_css: false,
  tiene_empleados: false,
  responsable_usuario_id: null,
};

export async function getConfig() {
  try {
    const { data, error } = await supabase.from('cont_config').select('*').eq('id', 1).maybeSingle();
    if (error || !data) return { ...CONFIG_DEFAULT };
    return data;
  } catch {
    return { ...CONFIG_DEFAULT };
  }
}

// Usuarios activos (para designar el responsable de tareas contables).
export async function listUsuarios() {
  const { data, error } = await supabase.from('usuarios').select('id, nombre').eq('activo', true).order('nombre');
  if (error) return [];
  return data || [];
}

export async function updateConfig(payload) {
  // upsert: si por alguna razón no existe la fila id=1, la crea; si existe, la
  // actualiza. Evita el error "Cannot coerce..." cuando no hay fila previa.
  const { data, error } = await supabase
    .from('cont_config')
    .upsert({ id: 1, ...payload, actualizado_en: new Date().toISOString() }, { onConflict: 'id' })
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ===========================================================================
// INGRESOS (derivados de la operación: tabla `comisiones`)
// ===========================================================================

// Ingreso REAL de RU = "RU Cobrado": la porción de RU (monto_ru) de las
// comisiones que ya fueron cerradas/liquidadas por RU (ru_liquidado_en).
// Es exactamente el mismo concepto que muestra la pestaña "RU Cobrado" del
// módulo de Comisiones, para que los números cuadren. Se reconoce el ingreso
// por la fecha de cierre (ru_liquidado_en).
export async function getIngresosPeriodo(desde, hasta) {
  const hastaFin = `${hasta}T23:59:59.999`; // ru_liquidado_en es timestamptz
  const { data, error } = await supabase
    .from('comisiones')
    .select('id, monto_ru, monto_agente, prima, ru_liquidado_en, periodo, estado, tipo, num_comprobante, ' +
            'polizas(numero, clientes(nombre), aseguradoras(nombre), ramos(nombre)), agentes(nombre)')
    .not('ru_liquidado_en', 'is', null)
    .gte('ru_liquidado_en', desde)
    .lte('ru_liquidado_en', hastaFin)
    .order('ru_liquidado_en', { ascending: false });
  if (error) { console.error('getIngresosPeriodo', error); return []; }
  return data || [];
}

// Resumen agregado de un rango. El ingreso es la comisión RU ya cobrada
// (cerrada). Las cuentas pendientes reflejan, desde la óptica de RU, lo que
// falta por realizar — alineado con el módulo de Comisiones.
export async function getResumen(desde, hasta) {
  const ingresos = await getIngresosPeriodo(desde, hasta);
  const comisionNeta = ingresos.reduce((s, c) => s + Number(c.monto_ru || 0), 0);

  // Otros ingresos registrados a mano (sobrecomisiones, bonos, fees, etc.)
  const manuales = await listIngresosManuales(desde, hasta);
  const otrosIngresos = manuales.reduce((s, i) => s + Number(i.monto || 0), 0);

  // Ingreso total del período = comisión RU cobrada + otros ingresos.
  const ingresoTotal = comisionNeta + otrosIngresos;

  const gastos = await listGastos(desde, hasta);
  const gastosTotal = gastos.reduce((s, g) => s + Number(g.total || 0), 0);
  const gastosDeducibles = gastos
    .filter(g => g.deducible)
    .reduce((s, g) => s + Number(g.monto || 0), 0); // base deducible sin ITBMS

  // Utilidad de caja: ingreso total menos los gastos.
  const utilidad = ingresoTotal - gastosTotal;
  // Renta neta gravable estimada (para ISR): ingreso total - gastos deducibles.
  const rentaNetaEstimada = ingresoTotal - gastosDeducibles;

  // Cuentas pendientes — óptica de RU (la contabilidad es de RU, no del agente):
  // - Comisión de RU que aún espera el pago de la aseguradora (pendiente_aseg).
  const ruEsperandoAseg = await sumComisionCampo('monto_ru', { estados: ['pendiente_aseg'] });
  // - Comisión de RU recibida de aseguradora y lista para liquidar (pendiente/aprobado).
  const ruListoLiquidar = await sumComisionCampo('monto_ru', { estados: ['pendiente', 'aprobado'] });
  // - Parte de los agentes pendiente de pagarles (pendiente/aprobado) = pasivo.
  const porPagarAgentes = await sumComisionCampo('monto_agente', { estados: ['pendiente', 'aprobado'] });

  return {
    comisionNeta, otrosIngresos, ingresoTotal,
    gastosTotal, gastosDeducibles, utilidad, rentaNetaEstimada,
    ruEsperandoAseg, ruListoLiquidar, porPagarAgentes,
    numIngresos: ingresos.length, numManuales: manuales.length, numGastos: gastos.length,
  };
}

// Suma un campo (monto_ru | monto_agente) de comisiones según un criterio.
// criterio: { estados?: string[], aprobadoNoLiquidado?: boolean }
async function sumComisionCampo(campo, criterio = {}) {
  let q = supabase.from('comisiones').select(campo);
  if (criterio.estados) q = q.in('estado', criterio.estados);
  if (criterio.aprobadoNoLiquidado) q = q.not('aprobado_en', 'is', null).is('ru_liquidado_en', null);
  const { data, error } = await q;
  if (error) return 0;
  return (data || []).reduce((s, c) => s + Number(c[campo] || 0), 0);
}

// ===========================================================================
// GASTOS (entrada manual)
// ===========================================================================
export async function listCategorias(tipo = 'gasto') {
  let q = supabase.from('cont_categorias').select('*').eq('activo', true);
  // tipo: 'gasto' | 'ingreso'. Tolerante si la columna aún no existe.
  q = q.eq('tipo', tipo).order('orden', { ascending: true });
  const { data, error } = await q;
  if (error) {
    // Fallback (por si la columna 'tipo' aún no está creada en la BD): trae todo.
    const { data: all } = await supabase.from('cont_categorias').select('*').eq('activo', true).order('orden');
    return (all || []).filter(c => (c.tipo || 'gasto') === tipo);
  }
  return data || [];
}

export async function listGastos(desde, hasta) {
  let q = supabase.from('cont_gastos').select('*, cont_categorias(nombre, deducible)');
  if (desde) q = q.gte('fecha', desde);
  if (hasta) q = q.lte('fecha', hasta);
  q = q.order('fecha', { ascending: false });
  const { data, error } = await q;
  if (error) { console.error('listGastos', error); return []; }
  return data || [];
}

export async function saveGasto(payload, id) {
  // Normaliza montos: total = monto + itbms
  const monto = Number(payload.monto || 0);
  const itbms = Number(payload.itbms || 0);
  const clean = { ...payload, monto, itbms, total: monto + itbms };
  if (id) {
    const { data, error } = await supabase.from('cont_gastos').update(clean).eq('id', id).select().single();
    if (error) throw error;
    return data;
  }
  const { data, error } = await supabase.from('cont_gastos').insert(clean).select().single();
  if (error) throw error;
  return data;
}

export async function deleteGasto(id) {
  const { error } = await supabase.from('cont_gastos').delete().eq('id', id);
  if (error) throw error;
  return true;
}

// ===========================================================================
// INGRESOS MANUALES (sobrecomisiones, bonos/concursos, fees, honorarios, etc.)
// Son ingresos que NO vienen del flujo automático de comisiones.
// ===========================================================================
export async function listIngresosManuales(desde, hasta) {
  let q = supabase.from('cont_ingresos').select('*, cont_categorias(nombre, causa_itbms)');
  if (desde) q = q.gte('fecha', desde);
  if (hasta) q = q.lte('fecha', hasta);
  q = q.order('fecha', { ascending: false });
  const { data, error } = await q;
  if (error) { console.error('listIngresosManuales', error); return []; }
  return data || [];
}

export async function saveIngreso(payload, id) {
  const monto = Number(payload.monto || 0);
  const itbms = Number(payload.itbms || 0);
  const clean = { ...payload, monto, itbms, total: monto + itbms };
  if (id) {
    const { data, error } = await supabase.from('cont_ingresos').update(clean).eq('id', id).select().single();
    if (error) throw error;
    return data;
  }
  const { data, error } = await supabase.from('cont_ingresos').insert(clean).select().single();
  if (error) throw error;
  return data;
}

export async function deleteIngreso(id) {
  const { error } = await supabase.from('cont_ingresos').delete().eq('id', id);
  if (error) throw error;
  return true;
}

// ===========================================================================
// OBLIGACIONES FISCALES (catálogo) y sus EVENTOS (vencimientos)
// ===========================================================================
export async function listObligaciones() {
  const { data, error } = await supabase
    .from('cont_obligaciones')
    .select('*')
    .eq('activo', true)
    .order('entidad', { ascending: true });
  if (error) return [];
  return data || [];
}

// Filtra el catálogo según la configuración del contribuyente.
export function obligacionesAplicables(obligaciones, config) {
  return (obligaciones || []).filter(o => {
    if (config.tipo_contribuyente === 'natural' && !o.aplica_natural) return false;
    if (config.tipo_contribuyente === 'juridica' && !o.aplica_juridica) return false;
    if (o.requiere_itbms && !config.inscrito_itbms) return false;
    if (o.requiere_css && !config.cotiza_css) return false;
    if (o.requiere_empleados && !config.tiene_empleados) return false;
    return true;
  });
}

export async function listEventos(anio) {
  const { data, error } = await supabase
    .from('cont_obligacion_eventos')
    .select('*, cont_obligaciones(nombre, entidad, tasa_referencia, codigo)')
    .like('periodo', `${anio}%`)
    .order('fecha_limite', { ascending: true });
  if (error) return [];
  return data || [];
}

// Genera (sin sobrescribir) los vencimientos del año para las obligaciones
// aplicables. Inserta solo los que falten; NUNCA modifica los existentes.
export async function generarEventosAnio(anio, config) {
  const obligaciones = obligacionesAplicables(await listObligaciones(), config);
  const filas = [];
  for (const o of obligaciones) {
    if (o.frecuencia === 'mensual') {
      for (let m = 1; m <= 12; m++) {
        filas.push(eventoFila(o, anio, m, o.dia_limite || 15));
      }
    } else if (o.frecuencia === 'trimestral') {
      [3, 6, 9, 12].forEach(m => filas.push(eventoFila(o, anio, m, o.dia_limite || 15)));
    } else { // anual
      filas.push(eventoFila(o, anio, o.mes_limite || 12, o.dia_limite || 31, true));
    }
  }
  if (!filas.length) return 0;
  const { error } = await supabase
    .from('cont_obligacion_eventos')
    .upsert(filas, { onConflict: 'obligacion_id,periodo', ignoreDuplicates: true });
  if (error) { console.error('generarEventosAnio', error); throw error; }
  return filas.length;
}

function eventoFila(o, anio, mes, dia, anual = false) {
  const mm = String(mes).padStart(2, '0');
  const periodo = anual ? `${anio}-A${mm}` : `${anio}-${mm}`;
  // Ajusta el día al último día del mes si se pasa (p.ej. 31 en febrero).
  const ultimo = new Date(anio, mes, 0).getDate();
  const d = Math.min(dia, ultimo);
  const fecha = `${anio}-${mm}-${String(d).padStart(2, '0')}`;
  return {
    obligacion_id: o.id,
    periodo,
    fecha_limite: fecha,
    estado: 'pendiente',
    monto_estimado: 0,
    monto_pagado: 0,
  };
}

export async function updateEvento(id, payload) {
  const { data, error } = await supabase.from('cont_obligacion_eventos').update(payload).eq('id', id).select().single();
  if (error) throw error;
  // Si se marcó pagada, cierra la gestión del CRM ligada (si existe).
  if (payload.estado === 'pagado') {
    await supabase.from('crm_gestiones').update({ estado: 'cerrado' })
      .eq('cont_evento_id', id).in('estado', ['abierto', 'en_proceso']);
  }
  return data;
}

// ===========================================================================
// INTEGRACIÓN CON EL CRM
// Crea gestiones en el CRM para las obligaciones y recomendaciones con fecha
// de cumplimiento dentro de los próximos `dias` días, asignadas al responsable
// de tareas contables. No duplica (usa cont_evento_id / cont_nota_id).
// ===========================================================================
export async function generarGestionesContables(config, dias = 60) {
  const responsable = config?.responsable_usuario_id;
  if (!responsable) return 0; // sin responsable designado, no se crea nada
  const ahora = new Date();
  const hoyStr = `${ahora.getFullYear()}-${String(ahora.getMonth() + 1).padStart(2, '0')}-${String(ahora.getDate()).padStart(2, '0')}`;
  const limite = new Date(ahora); limite.setDate(limite.getDate() + dias);
  const en = `${limite.getFullYear()}-${String(limite.getMonth() + 1).padStart(2, '0')}-${String(limite.getDate()).padStart(2, '0')}`;
  let creadas = 0;

  // Obligaciones (eventos) pendientes que vencen en <= dias.
  const { data: eventos } = await supabase
    .from('cont_obligacion_eventos')
    .select('id, fecha_limite, estado, cont_obligaciones(nombre, entidad, tasa_referencia)')
    .neq('estado', 'pagado')
    .lte('fecha_limite', en);
  for (const ev of eventos || []) {
    const { data: existe } = await supabase.from('crm_gestiones')
      .select('id').eq('cont_evento_id', ev.id).in('estado', ['abierto', 'en_proceso']).maybeSingle();
    if (existe) continue;
    const d = Math.ceil((new Date(ev.fecha_limite) - new Date(hoyStr)) / 86400000);
    await supabase.from('crm_gestiones').insert({
      tipo: 'obligacion',
      prioridad: d <= 15 ? 'alta' : 'normal',
      titulo: `Obligación: ${ev.cont_obligaciones?.nombre || ''}`.trim(),
      descripcion: `${ev.cont_obligaciones?.entidad || ''} — vence ${ev.fecha_limite}. ${ev.cont_obligaciones?.tasa_referencia || ''}`.trim(),
      estado: 'abierto',
      fecha_vencimiento: ev.fecha_limite,
      asignado_a: responsable,
      cont_evento_id: ev.id,
    });
    creadas++;
  }

  // Recomendaciones/consultas abiertas con fecha de cumplimiento en <= dias.
  const { data: notas } = await supabase
    .from('cont_notas')
    .select('id, titulo, tipo, fecha_limite, estado')
    .eq('estado', 'abierta')
    .not('fecha_limite', 'is', null)
    .lte('fecha_limite', en);
  for (const n of notas || []) {
    const { data: existe } = await supabase.from('crm_gestiones')
      .select('id').eq('cont_nota_id', n.id).in('estado', ['abierto', 'en_proceso']).maybeSingle();
    if (existe) continue;
    await supabase.from('crm_gestiones').insert({
      tipo: 'obligacion',
      prioridad: 'normal',
      titulo: `Contabilidad: ${n.titulo}`,
      descripcion: `${n.tipo === 'recomendacion' ? 'Recomendación' : n.tipo === 'situacion' ? 'Situación' : 'Consulta'} contable con fecha de cumplimiento ${n.fecha_limite}.`,
      estado: 'abierto',
      fecha_vencimiento: n.fecha_limite,
      asignado_a: responsable,
      cont_nota_id: n.id,
    });
    creadas++;
  }
  return creadas;
}

// ===========================================================================
// NOTAS: recomendaciones, consultas y situaciones a decidir
// ===========================================================================
export async function listNotas(estado) {
  let q = supabase.from('cont_notas').select('*');
  if (estado) q = q.eq('estado', estado);
  q = q.order('creado_en', { ascending: false });
  const { data, error } = await q;
  if (error) return [];
  return data || [];
}

export async function saveNota(payload, id) {
  if (id) {
    const { data, error } = await supabase.from('cont_notas').update(payload).eq('id', id).select().single();
    if (error) throw error;
    return data;
  }
  const { data, error } = await supabase.from('cont_notas').insert(payload).select().single();
  if (error) throw error;
  return data;
}

export async function resolverNota(id, respuesta) {
  const r = await saveNota({ estado: 'resuelta', respuesta, resuelto_en: new Date().toISOString() }, id);
  // Cierra la gestión del CRM ligada (si existe).
  await supabase.from('crm_gestiones').update({ estado: 'cerrado' })
    .eq('cont_nota_id', id).in('estado', ['abierto', 'en_proceso']);
  return r;
}

export async function deleteNota(id) {
  const { error } = await supabase.from('cont_notas').delete().eq('id', id);
  if (error) throw error;
  return true;
}

// ===========================================================================
// CÁLCULO DEL IMPUESTO SOBRE LA RENTA (ISR)
// Persona natural: tabla progresiva 2025/2026.
// Persona jurídica: 25% fijo sobre renta neta.
// ===========================================================================
export function calcularISR(rentaNeta, tipo) {
  const r = Number(rentaNeta || 0);
  if (r <= 0) return 0;
  if (tipo === 'juridica') return r * 0.25;
  // Persona natural (progresivo):
  if (r <= 11000) return 0;
  if (r <= 50000) return (r - 11000) * 0.15;
  return 5850 + (r - 50000) * 0.25;
}

// ===========================================================================
// MONTO ESTIMADO Y DETALLE POR OBLIGACIÓN
// ===========================================================================

// Agregados financieros de un año (para estimar las obligaciones).
export async function aggAnio(anio) {
  const desde = `${anio}-01-01`, hasta = `${anio}-12-31`;
  const comis = await getIngresosPeriodo(desde, hasta); // RU cobrado (cerrado) en el año
  const comisionRU = comis.reduce((s, c) => s + Number(c.monto_ru || 0), 0);
  const comisionGross = comis.reduce((s, c) => s + Number(c.monto_ru || 0) + Number(c.monto_agente || 0), 0);
  const manuales = await listIngresosManuales(desde, hasta);
  const otros = manuales.reduce((s, i) => s + Number(i.monto || 0), 0);
  const ingresoTotal = comisionRU + otros;
  const gastos = await listGastos(desde, hasta);
  const gastosDeducibles = gastos.filter(g => g.deducible).reduce((s, g) => s + Number(g.monto || 0), 0);
  const rentaNeta = ingresoTotal - gastosDeducibles;
  return { anio, comisionRU, comisionGross, otros, ingresoTotal, gastosDeducibles, rentaNeta };
}

// Contexto para estimar las obligaciones de un año del calendario.
export async function getContextoObligaciones(anio) {
  const [actual, anterior] = await Promise.all([aggAnio(anio), aggAnio(anio - 1)]);
  return { actual, anterior };
}

// Estima el monto a pagar de una obligación y devuelve el detalle resumido
// de cómo se calculó. Pura: recibe el evento, la config y el contexto.
// Devuelve { monto: number|null, detalle: [{label, valor?, texto?}], nota }.
export function calcularObligacion(ev, config, ctx) {
  const cod = ev?.cont_obligaciones?.codigo;
  const tipo = config?.tipo_contribuyente || 'natural';
  const A = ctx?.anterior || { anio: '', ingresoTotal: 0, gastosDeducibles: 0, rentaNeta: 0, comisionGross: 0 };

  switch (cod) {
    case 'isr': {
      const isr = calcularISR(A.rentaNeta, tipo);
      const d = desgloseISR(A.rentaNeta, tipo);
      return {
        monto: isr,
        detalle: [
          { label: `Ingreso total ${A.anio}`, valor: A.ingresoTotal },
          { label: '(−) Gastos deducibles', valor: A.gastosDeducibles },
          { label: '= Renta neta gravable', valor: A.rentaNeta },
          { label: 'Tramo aplicado', texto: d.tramo },
          { label: 'Tasa efectiva', texto: `${d.efectiva.toFixed(1)}%` },
        ],
        nota: `Año fiscal ${A.anio}. ${tipo === 'natural' ? 'Tabla progresiva (exento hasta $11,000).' : 'Tasa fija 25%.'}`,
      };
    }
    case 'estimado': {
      const isr = calcularISR(A.rentaNeta, tipo);
      return {
        monto: isr / 3,
        detalle: [
          { label: `ISR estimado del año ${A.anio}`, valor: isr },
          { label: '÷ 3 partidas (jun/sep/dic)', valor: isr / 3 },
        ],
        nota: 'Adelanto del impuesto: un tercio del ISR del año anterior.',
      };
    }
    case 'tasa_ssrp': {
      const base = A.comisionGross;
      const raw = base * 0.0025;
      const min = tipo === 'juridica' ? 500 : 100;
      const max = 5000;
      const monto = Math.min(Math.max(raw, min), max);
      return {
        monto,
        detalle: [
          { label: `Comisiones ${A.anio} (brutas)`, valor: base },
          { label: '× 0.25%', valor: raw },
          { label: `Mínimo $${min} / máximo $${max.toLocaleString('es-PA')}`, texto: 'aplicado' },
        ],
        nota: 'Sobre las comisiones del año anterior. Si el cálculo es menor al mínimo, se paga el mínimo.',
      };
    }
    case 'css': {
      const baseMensual = (A.ingresoTotal || 0) / 12;
      return {
        monto: baseMensual * 0.0936,
        detalle: [
          { label: `Ingreso promedio mensual (${A.anio})`, valor: baseMensual },
          { label: '× 9.36% (IVM)', valor: baseMensual * 0.0936 },
        ],
        nota: 'Estimación. La base la declaras tú a la CSS (mínimo de referencia $800/mes).',
      };
    }
    case 'tasa_unica':
      return { monto: 300, detalle: [{ label: 'Monto fijo anual', valor: 300 }], nota: 'Solo personas jurídicas.' };
    case 'fianza':
      return { monto: null, detalle: [{ label: 'Cobertura mínima', valor: 10000 }], nota: 'No es un pago: es mantener vigente la fianza ($10,000 mínimo de cobertura).' };
    case 'aviso':
      return { monto: null, detalle: [], nota: '2% del capital declarado de la empresa (mín $100). Requiere el monto del capital.' };
    case 'itbms':
      return { monto: null, detalle: [], nota: 'ITBMS cobrado menos ITBMS pagado del mes. Solo si estás inscrito como contribuyente.' };
    case 'planilla':
      return { monto: null, detalle: [], nota: 'Según los salarios de los empleados en planilla.' };
    case 'decimo':
      return { monto: null, detalle: [], nota: 'Un mes de salario prorrateado entre los empleados.' };
    default:
      return { monto: null, detalle: [], nota: 'Monto a definir manualmente.' };
  }
}

// Devuelve un desglose explicativo del ISR para mostrarlo al usuario.
export function desgloseISR(rentaNeta, tipo) {
  const r = Number(rentaNeta || 0);
  const isr = calcularISR(r, tipo);
  let tramo;
  if (tipo === 'juridica') tramo = '25% fijo sobre la renta neta';
  else if (r <= 11000) tramo = 'Exento (renta hasta $11,000)';
  else if (r <= 50000) tramo = '15% sobre el excedente de $11,000';
  else tramo = '$5,850 + 25% sobre el excedente de $50,000';
  const efectiva = r > 0 ? (isr / r) * 100 : 0;
  return { isr, tramo, efectiva };
}
